import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

export const DEFAULT_LIST_PAGE_SIZE = 20;

/** Altezza riga tabella desktop (allineata a py-2.5 + text-[13px]). */
export const LIST_TABLE_ROW_HEIGHT_CLASS = 'h-[42px]';

interface ListPaginationProps {
  page: number;
  totalPages: number;
  totalItems: number;
  pageSize?: number;
  onPageChange: (page: number) => void;
  disabled?: boolean;
  /** Optional status line centered between range text and page controls (e.g. background sync). */
  centerHint?: string;
  /** When true, total in "of N" is shown as "N+" (more items on server). */
  totalHasMore?: boolean;
  className?: string;
}

export function ListPagination({
  page,
  totalPages,
  totalItems,
  pageSize = DEFAULT_LIST_PAGE_SIZE,
  onPageChange,
  disabled = false,
  centerHint,
  totalHasMore = false,
  className,
}: ListPaginationProps) {
  const { t } = useLanguage();

  if (totalItems === 0) return null;

  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, totalItems);
  const canPrev = page > 1;
  const canNext = page < totalPages;

  return (
    <div className={cn('flex h-full w-full items-center justify-between gap-2', className)}>
      <p className="text-sm text-muted-foreground leading-none whitespace-nowrap shrink-0">
        {t('pagination.showing', {
          from,
          to,
          total: totalHasMore ? `${totalItems}+` : totalItems,
        })}
      </p>
      {centerHint ? (
        <p className="order-3 w-full sm:order-none sm:flex-1 sm:w-auto min-w-0 text-center text-xs text-muted-foreground leading-none truncate px-0 sm:px-2">
          {centerHint}
        </p>
      ) : (
        <div className="hidden sm:block flex-1 min-w-0" aria-hidden />
      )}
      <div className="flex items-center gap-2 shrink-0">
        <span className="text-sm text-muted-foreground leading-none whitespace-nowrap">
          {t('pagination.pageOf', { page, totalPages })}
        </span>
        <Button
          variant="outline"
          size="sm"
          className="h-7 gap-1 px-2.5 text-xs"
          disabled={disabled || !canPrev}
          onClick={() => onPageChange(page - 1)}
        >
          <ChevronLeft className="h-3.5 w-3.5" />
          {t('pagination.previous')}
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="h-7 gap-1 px-2.5 text-xs"
          disabled={disabled || !canNext}
          onClick={() => onPageChange(page + 1)}
        >
          {t('pagination.next')}
          <ChevronRight className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}
